/**
 * Quotations (design H): tabs with server counts, a search box, and a grid of
 * number, customer, validity, total, status and the customer's response.
 * Accepted (or still open) quotations convert to an invoice draft, which
 * opens in the editor. Rules live in quoteModel.ts.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import InvoiceRepository, { InvoiceListItem, ListResult } from '../../../internals/repository/InvoiceRepository';
import ShareDialog from '../components/ShareDialog';
import StatusChip from '../components/StatusChip';
import { errorText } from '../detailModel';
import { date, money, todayColombo } from '../format';
import { expiryHint, QUOTE_TABS, QuoteTab, quoteActions, quoteListQuery, responseText } from '../quoteModel';
import { useSelectedShop } from '../useInvoicing';

const COLS = '150px minmax(180px,1.2fr) 100px 120px 130px 110px minmax(200px,1.4fr) 150px 34px';

type LoadState = 'loading' | 'error' | 'ready';
interface MenuItem { label: string; run: () => void }

const CONVERT_ERRORS = { invalid_state: 'This quotation can no longer be converted. Reload to see its current state.' };

export default function QuotationListView() {
  const navigate = useNavigate();
  const shop = useSelectedShop();
  const shopId: string | undefined = shop?.shopId;
  const currency: string = shop?.currency || 'LKR';

  const [tab, setTab] = useState<QuoteTab>('ALL');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [items, setItems] = useState<InvoiceListItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [state, setState] = useState<LoadState>('loading');
  const [loadingMore, setLoadingMore] = useState(false);
  const [menu, setMenu] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [shareFor, setShareFor] = useState<InvoiceListItem | null>(null);
  const reqId = useRef(0);
  const today = todayColombo();

  useEffect(() => {
    const t = setTimeout(() => setQ(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  const apply = (r: ListResult, append: boolean) => {
    setItems((prev) => (append ? [...prev, ...r.items] : r.items));
    setNextCursor(r.nextCursor);
    setCounts(r.counts || {});
  };

  const load = useCallback(() => {
    if (!shopId) return;
    const id = ++reqId.current;
    setState('loading');
    setMenu(null);
    InvoiceRepository.list(shopId, quoteListQuery(tab, q))
      .then((r) => { if (id === reqId.current) { apply(r, false); setState('ready'); } })
      .catch(() => { if (id === reqId.current) setState('error'); });
  }, [shopId, tab, q]);

  useEffect(() => { load(); }, [load]);

  const loadMore = () => {
    if (!shopId || !nextCursor || loadingMore) return;
    const id = reqId.current;
    setLoadingMore(true);
    InvoiceRepository.list(shopId, { ...quoteListQuery(tab, q), cursor: nextCursor })
      .then((r) => { if (id === reqId.current) apply(r, true); })
      .catch((e) => setToast(errorText(e)))
      .finally(() => setLoadingMore(false));
  };

  // Escape or a click outside closes the row menu.
  useEffect(() => {
    if (!menu) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenu(null); };
    const onDown = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest?.('[data-row-menu]')) setMenu(null);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [menu]);

  const open = (it: InvoiceListItem, suffix = '') => navigate(`/invoices/${encodeURIComponent(it.docId)}${suffix}`);

  /** Runs a row action that ends on another page; failures show as a toast. */
  const act = async (it: InvoiceListItem, fn: () => Promise<string>, overrides: Record<string, string> = {}) => {
    if (!shopId || busyId) return;
    setMenu(null);
    setBusyId(it.docId);
    try {
      navigate(await fn());
    } catch (e) {
      setToast(errorText(e, overrides));
    } finally {
      setBusyId(null);
    }
  };

  const convert = (it: InvoiceListItem) => act(it, async () => {
    const { document } = await InvoiceRepository.convert(shopId!, it.docId, false);
    return `/invoices/${encodeURIComponent(document.docId)}/edit`;
  }, CONVERT_ERRORS);

  const duplicate = (it: InvoiceListItem) => act(it, async () => {
    const { document } = await InvoiceRepository.duplicate(shopId!, it.docId);
    return `/invoices/${encodeURIComponent(document.docId)}/edit`;
  });

  const menuFor = (it: InvoiceListItem): MenuItem[] => {
    const a = quoteActions(it);
    const m: MenuItem[] = [{ label: 'View', run: () => open(it) }];
    if (a.has('edit')) m.push({ label: 'Edit draft', run: () => open(it, '/edit') });
    if (a.has('send')) m.push({ label: 'Send or share', run: () => { setMenu(null); setShareFor(it); } });
    m.push({ label: 'Duplicate', run: () => duplicate(it) });
    if (a.has('convert')) m.push({ label: 'Convert to invoice', run: () => convert(it) });
    return m;
  };

  const newQuote = () => navigate('/invoices/new?docType=QUOTATION');
  const noneYet = tab === 'ALL' && !q.trim();

  return (
    <div className="h-screen flex flex-col w-full p-5 min-[1000px]:px-8 min-[1000px]:py-7 overflow-auto [&>*]:shrink-0 gap-[18px]">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="m-0 text-[28px] font-semibold tracking-[-0.015em] text-text1">Quotations</h1>
          <span className="text-sm text-text2">Customers accept or decline from the link. Accepted quotations convert to invoices.</span>
        </div>
        <button type="button" className="nl-btn nl-btn--primary" onClick={newQuote}>+ New quotation</button>
      </div>

      <div role="tablist" aria-label="Quotation status" className="flex gap-1 overflow-x-auto border-b border-borderc">
        {QUOTE_TABS.map((t) => {
          const on = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={on}
              onClick={() => setTab(t.key)}
              className={`-mb-px flex h-10 items-center gap-[7px] whitespace-nowrap border-b-2 bg-transparent px-3 text-sm font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand ${
                on ? 'border-brand text-brand' : 'border-transparent text-text2 hover:text-text1'
              }`}
            >
              {t.label}
              <span
                className={`grid h-[18px] min-w-[20px] place-items-center rounded-[9px] px-1.5 text-[11px] font-bold ${on ? 'bg-brand-soft text-brand' : 'text-text2'}`}
                style={on ? undefined : { background: 'var(--neutralSoft)' }}
              >
                {counts[t.key] ?? 0}
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search number, customer or phone"
          aria-label="Search quotations"
          className="nl-input nl-input--sm !h-9 max-w-[340px] flex-[1_1_260px] text-[13px]"
        />
        {search && (
          <button type="button" onClick={() => setSearch('')} className="bg-transparent px-1 text-[13px] font-semibold text-brand hover:underline">
            Clear search
          </button>
        )}
      </div>

      <div className="overflow-x-auto rounded-2xl border border-borderc bg-surface">
        <div className="min-w-[1120px]" role="table" aria-label="Quotations">
          <div
            role="row"
            className="grid items-center gap-3 bg-surface2 px-[18px] py-2.5 text-[11px] font-semibold uppercase tracking-[.07em] text-text3"
            style={{ gridTemplateColumns: COLS }}
          >
            <span role="columnheader">Number</span>
            <span role="columnheader">Customer</span>
            <span role="columnheader">Issued</span>
            <span role="columnheader">Valid until</span>
            <span role="columnheader" className="text-right">Total</span>
            <span role="columnheader">Status</span>
            <span role="columnheader">Customer response</span>
            <span />
            <span />
          </div>

          {state === 'loading' && [1, 2, 3, 4, 5].map((k) => (
            <div key={k} className="grid items-center gap-3 border-t border-borderc px-[18px] py-4" style={{ gridTemplateColumns: COLS }} aria-hidden>
              {[90, 100, 80, 80, 100].map((w, i) => <Bar key={i} w={`${w}%`} />)}
              <Bar w="70px" h={18} />
              <Bar w="80%" />
              <span />
              <span />
            </div>
          ))}

          {state === 'error' && (
            <div className="flex flex-col items-center gap-2.5 border-t border-borderc px-5 py-12 text-center">
              <span className="grid h-9 w-9 place-items-center rounded-full bg-neg-soft font-bold" style={{ color: 'var(--negFg)' }}>!</span>
              <span className="text-base font-semibold text-text1">Quotations could not load</span>
              <span className="max-w-[420px] text-text2">Check the connection and try again. Nothing has been lost.</span>
              <button type="button" className="nl-btn nl-btn--secondary nl-btn--sm !h-9" onClick={load}>Try again</button>
            </div>
          )}

          {state === 'ready' && items.length === 0 && (
            <div className="flex flex-col items-center gap-2.5 border-t border-borderc px-5 py-14 text-center">
              <span className="text-[17px] font-semibold text-text1">{noneYet ? 'No quotations yet' : 'No quotations here'}</span>
              <span className="max-w-[440px] text-text2">
                {noneYet
                  ? 'Send a quotation before an event. The customer can accept it from the link.'
                  : 'Try another tab, or clear the search.'}
              </span>
              {noneYet && <button type="button" className="nl-btn nl-btn--primary" onClick={newQuote}>+ New quotation</button>}
            </div>
          )}

          {state === 'ready' && items.map((it) => (
            <Row
              key={it.docId}
              it={it}
              currency={currency}
              today={today}
              busy={busyId === it.docId}
              onOpen={() => open(it)}
              onConvert={quoteActions(it).has('convert') ? () => convert(it) : undefined}
              menuOpen={menu === it.docId}
              onMenu={() => setMenu(menu === it.docId ? null : it.docId)}
              menu={menuFor(it)}
            />
          ))}
        </div>
      </div>

      {state === 'ready' && nextCursor && (
        <div className="flex justify-center pb-6">
          <button type="button" className="nl-btn nl-btn--secondary" onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}

      {shareFor && shopId && (
        <ShareDialog
          open
          onClose={() => setShareFor(null)}
          shopId={shopId}
          docId={shareFor.docId}
          document={shareFor}
          onShared={() => {}}
        />
      )}

      {toast && (
        <div
          role="status"
          className="fixed bottom-6 left-1/2 z-50 flex -translate-x-1/2 items-center gap-2 rounded-[10px] px-4 py-2.5 text-sm font-medium text-white"
          style={{ background: '#131318', boxShadow: 'var(--shadowLg)' }}
        >
          {toast}
        </div>
      )}
    </div>
  );
}

function Bar({ w, h = 10 }: { w: string; h?: number }) {
  return <span className="block animate-pulse" style={{ width: w, height: h, borderRadius: h / 2, background: 'var(--neutralSoft)' }} />;
}

function Row({ it, currency, today, busy, onOpen, onConvert, menuOpen, onMenu, menu }: {
  it: InvoiceListItem; currency: string; today: string; busy: boolean; onOpen: () => void; onConvert?: () => void;
  menuOpen: boolean; onMenu: () => void; menu: MenuItem[];
}) {
  const s = it.displayStatus;
  const hint = expiryHint(it.validUntil, s, today);
  const c = it.customer || {};
  const main = c.companyName || c.name || '—';
  const sub = c.companyName ? c.name : c.phoneE164;
  const label = it.number || 'Draft';
  const response = responseText(it);
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();

  return (
    <div
      role="row"
      data-testid="quotation-row"
      onClick={onOpen}
      className="grid cursor-pointer items-center gap-3 border-t border-borderc px-[18px] py-3 hover:bg-surface2"
      style={{ gridTemplateColumns: COLS }}
    >
      <button type="button" onClick={(e) => { stop(e); onOpen(); }} className="nl-num truncate bg-transparent p-0 text-left text-[13px] font-semibold text-text1">
        {label}
      </button>
      <span className="flex min-w-0 flex-col">
        <span className="truncate font-semibold text-text1">{main}</span>
        {sub && <span className={`truncate text-xs text-text3 ${c.companyName ? '' : 'nl-num'}`}>{sub}</span>}
      </span>
      <span className="text-[13px] text-text2">{date(it.issueDate)}</span>
      <span className="flex flex-col">
        <span className="text-[13px] text-text2">{it.validUntil ? date(it.validUntil) : '—'}</span>
        {hint && (
          <span className={`text-xs font-semibold ${hint.expired ? '' : 'text-text3'}`} style={hint.expired ? { color: 'var(--negFg)' } : undefined}>
            {hint.text}
          </span>
        )}
      </span>
      <span className="text-right font-semibold tabular-nums text-text1">{money(it.totalMinor, currency)}</span>
      <span><StatusChip status={s} /></span>
      <span className="truncate text-[13px] text-text2" title={response === '—' ? undefined : response}>{response}</span>
      <span className="flex justify-end" onClick={stop}>
        {onConvert && it.displayStatus === 'Q_ACCEPTED' && (
          <button
            type="button"
            disabled={busy}
            onClick={onConvert}
            className="h-[30px] whitespace-nowrap rounded-[7px] border border-brand bg-brand-soft px-2.5 text-[12px] font-semibold text-brand focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand disabled:opacity-60"
          >
            {busy ? 'Converting…' : 'Convert to invoice'}
          </button>
        )}
      </span>
      <div className="relative flex justify-end" data-row-menu onClick={stop}>
        <button
          type="button"
          onClick={onMenu}
          aria-label={`Actions for ${label}`}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          className="h-[30px] w-8 rounded-[7px] border border-borderc2 bg-surface text-[15px] font-bold leading-none text-text2 hover:bg-surface2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand"
        >
          ⋯
        </button>
        {menuOpen && (
          <div
            role="menu"
            className="absolute right-0 top-9 z-30 flex min-w-[200px] flex-col rounded-xl border border-borderc2 bg-surface p-1.5"
            style={{ boxShadow: 'var(--shadowLg)' }}
          >
            {menu.map((m, i) => (
              <button
                key={m.label}
                type="button"
                role="menuitem"
                autoFocus={i === 0}
                onClick={m.run}
                className="rounded-[7px] bg-transparent px-2.5 py-2 text-left text-[13px] font-medium text-text1 hover:bg-surface2 focus-visible:bg-surface2 focus-visible:outline-none"
              >
                {m.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
